import OrangeL2Widget from '@oskecare/fe-utilities/lib/OrangeL2Widget.js';
import {
  html,
  customElement,
  property,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound, mixin } from '@oskecare/fe-utilities/lib/decorators';
import { OUTAGE } from '@oskecare/fe-utilities/lib/constants/dataSources.js';
import {
  formatDate,
  formatDateTime,
  formatSn,
  formatTime,
} from '@oskecare/fe-utilities/scripts/format.js';
import i18n from '@oskecare/fe-utilities/lib/i18n.js';
import { translate, translateUnsafeHTML } from '@oskecare/fe-utilities/scripts/translator.js';
import sk from '../i18n/sk-SK.js';
import { config } from '../config.js';

const FUTURE_RELEVANCY_CUTOFF_DAYS = 7;

/**
 * Modal that informs users about planned service outages.
 */
@customElement('osk-outages-message')
@mixin(i18n({ 'sk-SK': sk }))
class OutagesMessage extends OrangeL2Widget {
  /**
   * @type {string}
   */
  logSource = 'outages-message';

  /**
   * @type {string}
   */
  @property({ attribute: 'outages-api-url', type: String })
  accessor outagesApiUrl = config.outagesApiUrl;

  /**
   * @type {{sn: string, nickname: string, address: ?object, outages: object[]}[]}
   */
  @state()
  accessor _outages = [];

  /**
   * @type {boolean}
   */
  @state()
  accessor _wasSeen = false;

  /**
   * Checks if any SN has any outages
   *
   * @return {boolean}
   */
  get hasOutages() {
    return this._outages.map(item => item.outages).flat().length > 0;
  }

  /**
   * Set response data from infrastructure and API calls, loading state during the call and error object if any is caught.
   *
   * @returns {Promise<void>}
   */
  async getData() {
    await super.getData({
      fetch: async () => {
        const customerData = await this.infrastructure.customerDataManager.getCustomerData();
        this._outages = (
          await Promise.all(
            customerData.snDataList.map(async item => {
              const outages = (
                await this.request({
                  method: 'GET',
                  route: this.outagesApiUrl,
                  query: { publicKey: item.sn },
                  dataSource: OUTAGE,
                  isNonessential: true,
                }).catch(() => [])
              )
                .map(outage => ({
                  duration: outage.outageDuration,
                  startDate: new Date(outage.outagePeriod.startDateTime),
                  endDate: new Date(outage.outagePeriod.endDateTime),
                }))
                .filter(outage => {
                  const startOfToday = new Date().setHours(0, 0, 0, 0);
                  const relevancyCutoff = new Date();
                  relevancyCutoff.setDate(relevancyCutoff.getDate() + FUTURE_RELEVANCY_CUTOFF_DAYS);
                  const isOutdated = outage.endDate < startOfToday;
                  const isRelevant = outage.startDate < relevancyCutoff;

                  return !isOutdated && isRelevant;
                });

              const address =
                outages.length > 0
                  ? await this.infrastructure.customerDataManager
                      .getInstalledBasicProducts(item.subscriptionId, true)
                      .then(installedBasicProducts => installedBasicProducts.installationAddress)
                      .catch(() => null)
                  : null;

              return {
                sn: item.sn,
                nickname: item.nickname,
                outages,
                address,
              };
            }),
          )
        ).filter(item => item.outages.length > 0);
      },
    });
  }

  @bound
  handleCloseModal() {
    this._wasSeen = true;
  }

  /**
   * Lifecycle method.
   *
   * @param {Map<string, string>} changedProperties
   */
  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.getData();
  }

  /**
   * Disables shadowRoot for this element.
   */
  createRenderRoot() {
    return this;
  }

  /**
   * @param {Date} startDate
   * @param {Date} endDate
   * @return {boolean}
   * @static
   */
  static isSameDay(startDate, endDate) {
    return (
      startDate.getDate() === endDate.getDate() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear()
    );
  }

  /**
   * @see OrangeL2Widget.render
   * @return {TemplateResult}
   */
  render() {
    return super.render({
      renderLoader: () => null,
      renderError: () => null,
      renderUi: () => {
        if (!this.hasOutages || this._wasSeen) {
          return null;
        }

        return html`
          <osk-modal trigger-id="outage-modal" @close=${this.handleCloseModal}>
            <h2 slot="modal-header">${translate('outages.title')}</h2>
            <div slot="modal-body">
              <div class="grid">
                <div class="grid__col--lg-8 align-self-start">
                  <p>${translate('outages.topDescription')}</p>
                  <ul class="list">
                    ${this._outages.map(
                      item => html`
                        <li class="list__item">
                          <p>
                            <b>${item.nickname}</b> (${formatSn(item.sn)})<br />
                            ${item.address
                              ? html`
                                  ${item.address.street} ${item.address.streetNumber}<br />
                                  ${item.address.postCode} ${item.address.city}<br />
                                `
                              : null}
                          </p>
                          ${item.outages.map(this.renderOutage)}
                        </li>
                      `,
                    )}
                  </ul>
                  <p>${translate('outages.bottomDescription')}</p>
                </div>
                <div class="grid__col--lg-4">
                  <img
                    class="img"
                    src="assets/images/working-man-with-error-screen.svg"
                    alt=${translate('outages.imageAltText')}
                    role="presentation"
                  />
                </div>
              </div>
            </div>
            <div slot="modal-footer">
              <div class="buttons">
                <a href="https://www.orange.sk/pomoc/nedostupnost-sluzieb" class="btn btn--primary"
                  >${translate('outages.buttons.learnMore')}</a
                >
                <button class="btn" @click=${this.handleCloseModal}>
                  ${translate('outages.buttons.understood')}
                </button>
              </div>
            </div>
          </osk-modal>
        `;
      },
    });
  }

  /**
   * @param {Object} outage
   * @return {TemplateResult}
   */
  @bound
  renderOutage(outage) {
    const durationText = outage.duration
      ? translateUnsafeHTML('outages.duration', {
          DURATION: {
            translateKey: 'outages.durationFormat',
            value: outage.duration,
          },
        })
      : null;

    if (OutagesMessage.isSameDay(outage.startDate, outage.endDate)) {
      return html`<p>
        ${translateUnsafeHTML('outages.sameDayInterval', {
          DATE: formatDate(outage.startDate),
          START_TIME: formatTime(outage.startDate),
          END_TIME: formatTime(outage.endDate),
        })}${durationText}.
      </p>`;
    }

    return html`<p>
      ${translateUnsafeHTML('outages.multiDayInterval', {
        START_DATE_TIME: formatDateTime(outage.startDate, this.locale, null),
        END_DATE_TIME: formatDateTime(outage.endDate, this.locale, null),
      })}${durationText}.
    </p>`;
  }
}

export default OutagesMessage;
